import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import palette from 'src/theme/palette';
import { getStatus, ChanegPropertyStatus } from 'src/ApiServices';

const ChangePropertyStatusModal = ({ 
  open, 
  onClose, 
  property, 
  onSave 
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const statusRes = await getStatus();
        if (statusRes?.data) {
          setStatusOptions(statusRes.data.map((s) => s.name));
        }
      } catch (error) {
        console.error('Failed to fetch status options:', error);
      }
    };

    if (open) {
      fetchStatusOptions();
    }
  }, [open]);

  useEffect(() => {
    if (property?.property_status) {
      setSelectedStatus(property.property_status);
    } else {
      setSelectedStatus('');
    }
  }, [property]);

  const handleSave = async () => {
    if (!property?.id || !selectedStatus) return;
    
    setLoading(true);
    try {
      await ChanegPropertyStatus(property.id, selectedStatus);
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to update property status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus(property?.property_status || '');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        borderBottom: `1px solid ${palette.divider}`,
        backgroundColor: palette.background.paper
      }}>
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            fontSize: '18px',
            color: palette.primary.titledark
          }}
        >
          Change Property Status
        </Typography>
        {property && (
          <Typography
            sx={{
              fontFamily: 'poppinsRegular',
              fontSize: '14px',
              color: palette.text.secondary,
              mt: 0.5
            }}
          >
            {property.title}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        <FormControl fullWidth variant="outlined">
          <InputLabel 
            id="property-status-label"
            shrink={true}
            sx={{
              fontFamily: 'poppinsRegular',
              fontSize: '16px',
              color: palette.text.primary,
              backgroundColor: palette.background.paper,
              px: 1,
              transform: 'translate(14px, -6px) scale(0.75)',
              '&.Mui-focused': {
                color: palette.primary.main,
              }
            }}
          >
            Available For
          </InputLabel>
          <Select
            labelId="property-status-label"
            value={selectedStatus || ''} // Ensure value is never undefined
            label="Available For"
            onChange={(e) => setSelectedStatus(e.target.value)}
            sx={{
              fontFamily: 'poppinsRegular',
              fontSize: '15px',
              '& .MuiSelect-select': {
                py: 1.5
              }
            }}
            notched={true}
          >
            {statusOptions.length > 0 ? (
              statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No status options available</MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2.5,
        gap: 1.5,
        borderTop: `1px solid ${palette.divider}`
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            fontFamily: 'poppinsMedium',
            fontSize: '14px',
            textTransform: 'none',
            px: 3.5
          }}
        >
          Close
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={!selectedStatus || loading}
          sx={{
            fontFamily: 'poppinsMedium',
            fontSize: '14px',
            textTransform: 'none',
            px: 3.5
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePropertyStatusModal;